import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { Org } from '../types/dynamodb-tables';

interface DistrictForm {
  name: string;
  country: string;
  state: string;
  phoneNumber: string;
  // Additional attributes from Org interface
  address?: string;
  city?: string;
  lang?: string;
  isMaster?: boolean;
  queryTags?: string[];
  idpDistrictId?: string;
  idpName?: string;
  idpId?: string;
  idp?: string;
  idpStatus?: string;
  isTMAllowed?: boolean;
  adult?: boolean;
  designatedDistrictChampion?: string;
  employerProvider?: string;
  secondary?: boolean;
  primary?: boolean;
}



interface SchoolForm {
  name: string;
  address: string;
  city: string;
  phoneNumber: string;
  country: string;
  state: string;
  // Additional attributes from Org interface
  lang?: string;
  isMaster?: boolean;
  queryTags?: string[];
  idpDistrictId?: string;
  idpName?: string;
  idpId?: string;
  idp?: string;
  idpStatus?: string;
  isTMAllowed?: boolean;
  adult?: boolean;
  designatedDistrictChampion?: string;
  employerProvider?: string;
  secondary?: boolean;
  primary?: boolean;
}

interface DistrictSearchParams {
  name: string;
  noChamps: boolean;
}

interface SchoolSearchParams {
  parentOrgId: string | null;
  name: string;
}

interface OrgDataStore {
  // Form states
  districtForm: DistrictForm;
  schoolForm: SchoolForm;
  
  // Search params
  districtSearchParams: DistrictSearchParams;
  schoolSearchParams: SchoolSearchParams;

  // UI state
  isFilterPanelOpen: boolean;
  selectedDistrict: Org | null;
  selectedSchool: Org | null;
  schoolSelectorWarning: boolean; // New state for highlighting school selector
  refetchTrigger: number; // Trigger to force refetch of organization data

  // Actions
  setDistrictForm: (formData: Partial<DistrictForm>) => void;
  resetDistrictForm: () => void;
  setSchoolForm: (formData: Partial<SchoolForm>) => void;
  resetSchoolForm: () => void;
  setDistrictSearchParams: (params: Partial<DistrictSearchParams>) => void;
  setSchoolSearchParams: (params: Partial<SchoolSearchParams>) => void;
  toggleFilterPanel: () => void;
  setSelectedDistrict: (district: Org) => void;
  setSelectedSchool: (school: Org | null) => void;
  clearSelectedSchool: () => void;
  clearSelectedDistrict: () => void;
  setSchoolSelectorWarning: (warning: boolean) => void; // New action
  triggerRefetch: () => void; // Trigger to force refetch
  // Helper to clear all persisted selector state
  clearPersistedSelectors: () => void;
  updateOrganizationLogo: (orgId: string, logo: string | undefined) => void;
}

const useOrgDataStore = create<OrgDataStore>()(
  devtools(
    persist(
      (set) => ({
        // District form state
        districtForm: {
          name: '',
          country: 'US',
          state: '',
          phoneNumber: '',
          // Additional attributes initialized as undefined
          address: undefined,
          city: undefined,
          lang: undefined,
          isMaster: undefined,
          queryTags: undefined,
          idpDistrictId: undefined,
          idpName: undefined,
          idpId: undefined,
          idp: undefined,
          idpStatus: undefined,
          isTMAllowed: undefined,
          adult: undefined,
          designatedDistrictChampion: undefined,
          employerProvider: undefined,
          secondary: undefined,
          primary: undefined,
        },
        
        // School form state
        schoolForm: {
          name: '',
          address: '',
          city: '',
          phoneNumber: '',
          country: 'US',
          state: '',
          // Additional attributes initialized as undefined
          lang: undefined,
          isMaster: undefined,
          queryTags: undefined,
          idpDistrictId: undefined,
          idpName: undefined,
          idpId: undefined,
          idp: undefined,
          idpStatus: undefined,
          isTMAllowed: undefined,
          adult: undefined,
          designatedDistrictChampion: undefined,
          employerProvider: undefined,
          secondary: undefined,
          primary: undefined,
        },
        
        // Search params
        districtSearchParams: {
          name: '',
          noChamps: false,
        },
        
        schoolSearchParams: {
          parentOrgId: null,
          name: '',
        },

        // Actions for form state management
        setDistrictForm: (formData) => {
          set((state) => ({
            districtForm: { ...state.districtForm, ...formData }
          }));
        },
        
        resetDistrictForm: () => {
          set({
            districtForm: {
              name: '',
              country: 'US',
              state: '',
              phoneNumber: '',
              // Reset additional attributes to undefined
              address: undefined,
              city: undefined,
              lang: undefined,
              isMaster: undefined,
              queryTags: undefined,
              idpDistrictId: undefined,
              idpName: undefined,
              idpId: undefined,
              idp: undefined,
              idpStatus: undefined,
              isTMAllowed: undefined,
              adult: undefined,
              designatedDistrictChampion: undefined,
              employerProvider: undefined,
              secondary: undefined,
              primary: undefined,
            }
          });
        },
        
        setSchoolForm: (formData) => {
          set((state) => ({
            schoolForm: { ...state.schoolForm, ...formData }
          }));
        },
        

        
        resetSchoolForm: () => {
          set({
            schoolForm: {
              name: '',
              address: '',
              city: '',
              phoneNumber: '',
              country: 'US',
              state: '',
              // Reset additional attributes to undefined
              lang: undefined,
              isMaster: undefined,
              queryTags: undefined,
              idpDistrictId: undefined,
              idpName: undefined,
              idpId: undefined,
              idp: undefined,
              idpStatus: undefined,
              isTMAllowed: undefined,
              adult: undefined,
              designatedDistrictChampion: undefined,
              employerProvider: undefined,
              secondary: undefined,
              primary: undefined,
            }
          });
        },
        
        // Search state management
        setDistrictSearchParams: (params) => {
          set((state) => ({
            districtSearchParams: { ...state.districtSearchParams, ...params }
          }));
        },
        
        setSchoolSearchParams: (params) => {
          set((state) => ({
            schoolSearchParams: { ...state.schoolSearchParams, ...params }
          }), false, 'setSchoolSearchParams/success');
        },
        
        // UI state flags
        isFilterPanelOpen: false,
        toggleFilterPanel: () => set((state) => ({ isFilterPanelOpen: !state.isFilterPanelOpen })),
        
        selectedDistrict: null,
        setSelectedDistrict: (district: Org) => set(state =>  ({ 
          selectedDistrict: district,
          schoolSearchParams: {
            ...state.schoolSearchParams,
            parentOrgId: district.orgId || null
          },
        }), false, 'setSelectedDistrict/success'),
        
        selectedSchool: null,
        setSelectedSchool: (school: Org | null) => set({ selectedSchool: school }, false, 'setSelectedSchool/success'),

        clearSelectedSchool: () => set({ 
          selectedSchool: null,
          schoolSearchParams: {
            parentOrgId: null,
            name: ''
          },
        }, false, 'clearSelectedSchool/success'),

        clearSelectedDistrict: () => set({ 
          selectedDistrict: null,
          selectedSchool: null,
          schoolSearchParams: {
            parentOrgId: null,
            name: ''
          },
        }, false, 'clearSelectedDistrict/success'),

                schoolSelectorWarning: false,
        setSchoolSelectorWarning: (warning: boolean) => set({ schoolSelectorWarning: warning }, false, 'setSchoolSelectorWarning/success'),

        refetchTrigger: 0,
        triggerRefetch: () => set((state) => ({ refetchTrigger: state.refetchTrigger + 1 }), false, 'triggerRefetch/success'),

        // Helper to clear all persisted selector state
        clearPersistedSelectors: () => set({
          selectedDistrict: null,
          selectedSchool: null,
          schoolSearchParams: {
            parentOrgId: null,
            name: ''
          },
        }, false, 'clearPersistedSelectors/success'),

        // Update organization logo in store
        updateOrganizationLogo: (orgId: string, logo: string | undefined) => set((state) => {
          const updates: Partial<OrgDataStore> = {};
          
          // Update selectedDistrict if it matches the orgId
          if (state.selectedDistrict?.orgId === orgId) {
            updates.selectedDistrict = {
              ...state.selectedDistrict,
              logo: logo || undefined
            };
          }
          
          // Update selectedSchool if it matches the orgId
          if (state.selectedSchool?.orgId === orgId) {
            updates.selectedSchool = {
              ...state.selectedSchool,
              logo: logo || undefined
            };
          }
          
          return updates;
        }, false, 'updateOrganizationLogo/success'),
      }),
      {
        name: 'org-store-persist',
        // Only persist the selector state, not form data or search params
        // This ensures district and school selections persist across new windows/tabs
        partialize: (state) => ({
          selectedDistrict: state.selectedDistrict,
          selectedSchool: state.selectedSchool,
        }),
      }
    ),
    {
      name: 'org-store',
      enabled: import.meta.env.DEV // Only enable devtools in development
    }   
  )
);

export default useOrgDataStore; 